/**
 * /saas/crm — CRM Deal Queue.
 *
 * Shows all queued deals from connected CRMs. Accountants review pending deals,
 * pick a plan + customer, and activate — creating a subscription in one click.
 *
 * API surface:
 *   GET  /api/saas/crm/connections                list connections (for empty-state link)
 *   GET  /api/saas/crm/deals?status=...           list deal queue
 *   POST /api/saas/crm/deals/{id}/fetch           re-trigger detail fetch
 *   POST /api/saas/crm/deals/{id}/activate        activate → create subscription
 *   POST /api/saas/crm/deals/{id}/skip            skip deal
 *   GET  /api/saas/plans                          plan list for picker
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { api, errorMessage } from "@/lib/api";

export const Route = createFileRoute("/_auth/saas/crm")({
  component: CrmDealsPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

type DealStatus = "pending_fetch" | "pending_review" | "activated" | "skipped" | "error";

interface Deal {
  id: string;
  provider: string;
  provider_deal_id: string;
  deal_name: string | null;
  customer_name: string | null;
  customer_email: string | null;
  close_date: string | null;
  amount: string | null;
  currency: string;
  matched_plan_id: string | null;
  matched_party_id: string | null;
  billing_interval: string | null;
  status: DealStatus;
  subscription_id: string | null;
  review_note: string | null;
  created_at: string;
}

interface Plan {
  id: string;
  code: string;
  name: string;
  amount: string;
  currency_code: string;
  interval: string;
}

interface CrmConnection {
  id: string;
  provider: string;
  status: string;
}

// ── Status chip ───────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<DealStatus, string> = {
  pending_fetch:  "Fetching…",
  pending_review: "Pending review",
  activated:      "Activated",
  skipped:        "Skipped",
  error:          "Error",
};

const STATUS_COLOR: Record<DealStatus, "default" | "warning" | "success" | "error" | "info"> = {
  pending_fetch:  "info",
  pending_review: "warning",
  activated:      "success",
  skipped:        "default",
  error:          "error",
};

function DealStatusChip({ status }: { status: DealStatus }) {
  return (
    <Chip
      label={STATUS_LABEL[status] ?? status}
      color={STATUS_COLOR[status] ?? "default"}
      size="small"
    />
  );
}

// ── Tab filter options ────────────────────────────────────────────────────────

const TABS: Array<{ value: string; label: string }> = [
  { value: "pending_review", label: "Pending review" },
  { value: "",               label: "All" },
  { value: "activated",      label: "Activated" },
  { value: "skipped",        label: "Skipped" },
  { value: "error",          label: "Error / Fetching" },
];

// ── Format helpers ────────────────────────────────────────────────────────────

function fmtAmount(amount: string | null, currency: string): string {
  if (!amount) return "—";
  const n = parseFloat(amount);
  if (isNaN(n)) return amount;
  return n.toLocaleString("en-US", { style: "currency", currency: currency || "USD" });
}

// ── Review drawer ─────────────────────────────────────────────────────────────

function ReviewDrawer({
  deal,
  plans,
  onClose,
  onActivated,
  onSkipped,
}: {
  deal: Deal | null;
  plans: Plan[];
  onClose: () => void;
  onActivated: (subNo: string) => void;
  onSkipped: () => void;
}) {
  const qc = useQueryClient();

  const [planId, setPlanId] = useState<string>("");
  const [customerName, setCustomerName] = useState<string>("");
  const [billingInterval, setBillingInterval] = useState<string>("monthly");
  const [overridePrice, setOverridePrice] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [skipNote, setSkipNote] = useState<string>("");
  const [skipOpen, setSkipOpen] = useState(false);

  // Sync form fields when drawer opens with a new deal
  const dealId = deal?.id;
  if (dealId) {
    // Using imperative init so we don't need useEffect
  }

  const initFormForDeal = (d: Deal) => {
    setPlanId(d.matched_plan_id ?? "");
    setCustomerName(d.customer_name ?? "");
    setBillingInterval(d.billing_interval ?? "monthly");
    setOverridePrice("");
    setNotes("");
    setSkipNote("");
    setSkipOpen(false);
  };

  // When deal changes reset the form
  const [lastDealId, setLastDealId] = useState<string | null>(null);
  if (deal && deal.id !== lastDealId) {
    setLastDealId(deal.id);
    initFormForDeal(deal);
  }

  const activateMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ subscription_id: string; subscription_no: string; status: string }>(
        `/api/saas/crm/deals/${deal!.id}/activate`,
        { method: "POST", body },
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["crm-deals"] });
      onActivated(data.subscription_no);
      onClose();
    },
  });

  const skipMut = useMutation({
    mutationFn: (note: string) =>
      api(`/api/saas/crm/deals/${deal!.id}/skip`, {
        method: "POST",
        body: { note: note || null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-deals"] });
      onSkipped();
      onClose();
    },
  });

  const fetchMut = useMutation({
    mutationFn: () =>
      api(`/api/saas/crm/deals/${deal!.id}/fetch`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-deals"] });
    },
  });

  if (!deal) return null;

  const isPending = deal.status === "pending_review" || deal.status === "pending_fetch";
  const isError = deal.status === "error";
  const canActivate = deal.status === "pending_review";

  const handleActivate = () => {
    const body: Record<string, unknown> = {};
    if (planId) body.plan_id = planId;
    if (customerName.trim()) body.customer_name = customerName.trim();
    if (billingInterval) body.billing_interval = billingInterval;
    if (overridePrice.trim()) body.override_price = overridePrice.trim();
    if (notes.trim()) body.notes = notes.trim();
    activateMut.mutate(body);
  };

  return (
    <Drawer anchor="right" open={!!deal} onClose={onClose} PaperProps={{ sx: { width: 440 } }}>
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {deal.deal_name ?? "Untitled deal"}
            </Typography>
            <Chip
              label={deal.provider}
              size="small"
              variant="outlined"
              sx={{ mt: 0.5, textTransform: "capitalize" }}
            />
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Deal info */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "grey.50" }}>
          <Stack spacing={0.5}>
            <InfoRow label="Status" value={<DealStatusChip status={deal.status} />} />
            <InfoRow label="Amount" value={fmtAmount(deal.amount, deal.currency)} />
            <InfoRow label="Close date" value={deal.close_date ?? "—"} />
            <InfoRow label="Company" value={deal.customer_name ?? "—"} />
            {deal.customer_email && (
              <InfoRow label="Email" value={deal.customer_email} />
            )}
            <InfoRow label="Deal ID" value={
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                {deal.provider_deal_id}
              </Typography>
            } />
            {deal.subscription_id && (
              <InfoRow label="Subscription" value={
                <Typography variant="caption" color="success.main">
                  Created ✓
                </Typography>
              } />
            )}
          </Stack>
        </Paper>

        {isError && (
          <Alert
            severity="error"
            variant="outlined"
            sx={{ mb: 2 }}
            action={
              <Button
                size="small"
                startIcon={fetchMut.isPending ? <CircularProgress size={12} /> : <RefreshIcon />}
                disabled={fetchMut.isPending}
                onClick={() => fetchMut.mutate()}
              >
                Retry
              </Button>
            }
          >
            Detail fetch failed. Retry to pull fresh data from the CRM.
          </Alert>
        )}

        {deal.status === "pending_fetch" && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Fetching deal details from {deal.provider}… refresh in a moment.
          </Alert>
        )}

        {/* Activate form — only shown for review-ready deals */}
        {(canActivate || isError) && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              ACTIVATE
            </Typography>

            {/* Plan picker */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Plan</InputLabel>
              <Select
                value={planId}
                label="Plan"
                onChange={(e) => setPlanId(e.target.value)}
              >
                <MenuItem value="">
                  <em>— select a plan —</em>
                </MenuItem>
                {plans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                    <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                      {parseFloat(p.amount).toLocaleString("en-US", {
                        style: "currency",
                        currency: p.currency_code,
                      })}/{p.interval}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Customer name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              helperText="Leave blank to use the matched party or company name from CRM"
            />

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Billing interval</InputLabel>
              <Select
                value={billingInterval}
                label="Billing interval"
                onChange={(e) => setBillingInterval(e.target.value)}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="annual">Annual</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Price override (optional)"
              value={overridePrice}
              onChange={(e) => setOverridePrice(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              placeholder="Leave blank to use plan price"
            />

            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
          </>
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Actions */}
        {canActivate && (
          <Stack spacing={1}>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                activateMut.isPending ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />
              }
              disabled={activateMut.isPending || skipMut.isPending}
              onClick={handleActivate}
              fullWidth
            >
              {activateMut.isPending ? "Activating…" : "Activate — create subscription"}
            </Button>
            {activateMut.isError && (
              <Alert severity="error" variant="outlined" sx={{ fontSize: 12 }}>
                {errorMessage(activateMut.error)}
              </Alert>
            )}
            <Button
              variant="outlined"
              color="warning"
              startIcon={<SkipNextIcon />}
              disabled={activateMut.isPending || skipMut.isPending}
              onClick={() => setSkipOpen(true)}
              fullWidth
            >
              Skip this deal
            </Button>
          </Stack>
        )}

        {deal.status === "skipped" && (
          <Alert severity="info" variant="outlined">
            Skipped{deal.review_note ? ` — "${deal.review_note}"` : ""}
          </Alert>
        )}

        {deal.status === "activated" && (
          <Alert severity="success" variant="outlined">
            Subscription created.
          </Alert>
        )}
      </Box>

      {/* Skip confirm dialog */}
      <Dialog open={skipOpen} onClose={() => setSkipOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Skip this deal?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Skipped deals are excluded from billing and won't reappear in the pending queue.
          </Typography>
          <TextField
            label="Note (optional)"
            fullWidth
            multiline
            rows={2}
            size="small"
            value={skipNote}
            onChange={(e) => setSkipNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            disabled={skipMut.isPending}
            onClick={() => skipMut.mutate(skipNote)}
          >
            Skip
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
        {label}
      </Typography>
      <Typography variant="caption" textAlign="right">
        {value}
      </Typography>
    </Stack>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function CrmDealsPage() {
  const qc = useQueryClient();
  const [tabValue, setTabValue] = useState("pending_review");
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);

  const { data: connections = [] } = useQuery<CrmConnection[]>({
    queryKey: ["crm-connections"],
    queryFn: () => api<CrmConnection[]>("/api/saas/crm/connections"),
  });

  const { data: deals = [], isLoading } = useQuery<Deal[]>({
    queryKey: ["crm-deals", tabValue],
    queryFn: () =>
      api<Deal[]>(`/api/saas/crm/deals${tabValue ? `?status=${tabValue}` : ""}`),
    refetchInterval: 10_000, // poll every 10s so pending_fetch rows refresh
  });

  const { data: plans = [] } = useQuery<Plan[]>({
    queryKey: ["saas-plans"],
    queryFn: () => api<Plan[]>("/api/saas/plans"),
  });

  const hasConnections = connections.some((c) => c.status === "active");

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            CRM Deal Queue
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Deals from connected CRMs that closed won. Review each deal, confirm the plan and
            customer, and activate to create a subscription.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              qc.invalidateQueries({ queryKey: ["crm-deals"] });
            }}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
          <Button size="small" variant="outlined" href="/settings/crm">
            Manage connections
          </Button>
        </Stack>
      </Stack>

      {/* Empty state — no connections */}
      {!isLoading && !hasConnections && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", mb: 3 }}>
          <Typography variant="h6" fontWeight={500} gutterBottom>
            No CRM connected
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Connect HubSpot or Salesforce to start receiving deals automatically.
          </Typography>
          <Button variant="contained" href="/settings/crm">
            Set up CRM connector
          </Button>
        </Paper>
      )}

      {/* Tab filter */}
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {/* Deal table */}
      {isLoading ? (
        <CircularProgress size={28} />
      ) : deals.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {tabValue === "pending_review"
              ? "No deals pending review — you're all caught up."
              : "No deals in this status."}
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Deal</TableCell>
                <TableCell>Company</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Close date</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Received</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deals.map((deal) => (
                <TableRow
                  key={deal.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedDeal(deal)}
                  selected={selectedDeal?.id === deal.id}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={deal.status === "pending_review" ? 600 : 400}>
                      {deal.deal_name ?? (
                        <Typography component="span" variant="caption" color="text.secondary">
                          #{deal.provider_deal_id}
                        </Typography>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {deal.customer_name ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {fmtAmount(deal.amount, deal.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {deal.close_date ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={deal.provider}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell>
                    <DealStatusChip status={deal.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(deal.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Review drawer */}
      <ReviewDrawer
        deal={selectedDeal}
        plans={plans}
        onClose={() => setSelectedDeal(null)}
        onActivated={(subNo) => {
          setSnack({ msg: `Subscription ${subNo} created.`, severity: "success" });
        }}
        onSkipped={() => {
          setSnack({ msg: "Deal skipped.", severity: "info" });
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack?.severity ?? "info"} onClose={() => setSnack(null)} variant="filled">
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
